import { domain } from '.'
import {
  BIZ_SUZHI, BIZ_GAOZHOGN, BIZ_XINXUEKE, BIZ_WANGXIAO,
  SCHEMA_GAOZHOGN, SCHEMA_XINXUEKE, SCHEMA_WANGXIAO,
  QUERY_BIZID_NAME, BIZ_ID_KEY
} from './const'
import {
  isInWangxiaoApp, isInGaozhongApp, isInXinxuekeApp,
  isInXueersiBrowser, isInXinXuekeBrowser, isInVIPXBrowser,
  isInApp, queryStringToObject, getCurrentUA, setHeadFavicon
} from './lib'

/**
 * 返回 biz
 * @returns number
 */
export const getCurrentBizId = () => {
  if (isInApp()) { // 端 内
    if (isInXinxuekeApp()) {
      return BIZ_XINXUEKE
    } else if (isInGaozhongApp()) {
      return BIZ_GAOZHOGN
    } else if (isInWangxiaoApp()) { // 网校的app, 返回素质的bizid
      return BIZ_SUZHI
    } else {
      throw Error(`环境UA错误, ${getCurrentUA()} `)
    }
  } else { // 端 外
    // 获取url参数
    const queryObject = queryStringToObject(window.location.search) || {}
    const BIZ_ID = +queryObject[QUERY_BIZID_NAME]
    if (isInXueersiBrowser()) { // 网校域名下面 如果没有,默认网校
      // 判断bizId是否符合预设值
      const hasBizId = [BIZ_SUZHI, BIZ_WANGXIAO].indexOf(BIZ_ID) > -1
      // 如果不符合，默认取网校
      return hasBizId ? BIZ_ID : BIZ_WANGXIAO
    } else if (isInXinXuekeBrowser()) { // 学科域名下面，不从URL取值，直接返回常量
      return BIZ_XINXUEKE
    } else if (isInVIPXBrowser()) { // 高中域名下面，不从URL取值，直接返回常量
      return BIZ_GAOZHOGN
    } else {
      throw Error(`环境配置错误 biz: ${queryObject[QUERY_BIZID_NAME]}, hostname: ${window.location.hostname}`)
    }
  }
}

/**
 * 返回当前biz对象
 * @returns {object} {X-Businessline-Id:10}
 */
export const getCurrentBizKeyValueObject = () => {
  try {
    const currentBizID = getCurrentBizId()
    return { [BIZ_ID_KEY]: currentBizID }
  } catch (error) {
    throw Error(error)
  }
}

/**
 * 网校
 * @returns boolean
 */
export const isWangxiaoBiz = () => {
  return getCurrentBizId() === BIZ_WANGXIAO
}

/**
 * 素质
 * @returns boolean
 */
export const isSuZhiBiz = () => {
  return getCurrentBizId() === BIZ_SUZHI
}

/**
 * 高中
 * @returns boolean
 */
export const isGaozhongBiz = () => {
  return getCurrentBizId() === BIZ_GAOZHOGN
}

/**
 * 新学科
 * @returns boolean
 */
export const isXuekeBiz = () => {
  return getCurrentBizId() === BIZ_XINXUEKE
}

/**
 * 获取domain: 默认为//开头, host参数必填
 * @param {object} [options] - 参数对象
 * @param {string} [options.protocol=//] - 协议: 默认//
 * @param {string} options.host - 域名 必填参数
 * @param {string} [options.path=''] - 路径 默认''
 * @param {boolean} [options.prefix=true]
 * @returns string
 */
export const getDomain = (options) => {
  const { protocol, host, path = '', prefix = true } = options || {}
  if (!host) throw Error(`必须传入参数: ${host}!`)
  return `${protocol ? protocol + ':' : ''}${prefix ? '//' : ''}${host.replace(/^\/\/+/, '')}${path}`
}

/**
 * 返回新学科（K9）的地址
 * @param {object} options
 * @returns url
 */
export const handleXuekeUrl = options => {
  const { name, isApi = true, ...rest } = options
  // 网关配置：https://wiki.zhiyinlou.com/pages/viewpage.action?pageId=167247128
  let baseUrl = getDomain({ host: domain.XES_SUBJECT_DOMAIN })
  if (isApi) {
    baseUrl += '/api'
  } else {
    // 注意：需要满足原二级域名与更改后的一致才可使用, 否则推荐 getCustomParams 方法
    baseUrl += '/page'
  }
  return getDomain({ host: baseUrl + '/' + name, ...rest })
}

/**
 * 返回高中的地址
 * @param {object} options
 * @returns url
 */
export const handleGaoZhongUrl = options => {
  const { name, isApi = true, ...rest } = options
  // 网关配置：https://wiki.zhiyinlou.com/pages/viewpage.action?pageId=167247128
  let baseUrl = getDomain({ host: domain.XES_VIPX_DOMAIN })
  if (isApi) {
    baseUrl += '/api'
  } else {
    // 注意：需要满足原二级域名与更改后的一致才可使用，否则推荐 getCustomParams 方法
    baseUrl += '/page'
  }
  return getDomain({ host: baseUrl + '/' + name, ...rest })
}

/**
 * 学而思、素质公用一个域名;
 * 新学科, 高中单独一个;
 * @param {object} [options] - 参数对象
 * @param {string} options.name - 二级域名名称
 * @param {string} options.isApi - 学科是否为接口地址
 * @requires getDomain 同时支持该方法传参
 * @returns string
 */
export const getHostUrl = (options = {}) => {
  const { name, ...rest } = options
  // 学而思、素质公用一个域名
  if (isWangxiaoBiz() || isSuZhiBiz()) {
    return getDomain({ host: getXesURL(name), ...rest })
  } else if (isXuekeBiz()) { // 新学科
    return handleXuekeUrl(options)
  } else if (isGaozhongBiz()) { // 高中
    return handleGaoZhongUrl(options)
  } else {
    throw Error('请检查（biz|域名）是否配置正确！')
  }
}

/**
 * 传入二级域名，返回学而思地址. 仅限返回已定义地址
 * 例如 touch -> touch.xueersi.com
 * @param {string} name
 * @returns string
 */
export const getXesURL = (name) => {
  const url = domain[`XES_${name.toUpperCase()}_DOMAIN`]
  if (!url) throw Error(`未在变量声明中查找到符合 ${name} 的配置!`)
  return url
}

/**
 * 获取设备schema
 * @returns string
 */
export const getSchema = () => {
  if (isGaozhongBiz()) { // 高中
    return SCHEMA_GAOZHOGN
  } else if (isXuekeBiz()) { // 新学科
    return SCHEMA_XINXUEKE
  } else if (isWangxiaoBiz() || isSuZhiBiz()) { // 网校、素质
    return SCHEMA_WANGXIAO
  } else {
    throw Error('获取设备schema出错了,请检查!')
  }
}

/**
 * 根据传入的参数，返回（新学科、学而思、高中）中符合条件的参数
 * @param {object} options
 * @param {string} options.xes - 学而思、素质
 * @param {string} options.grade - 高中
 * @param {string} options.subject - 新学科
 * @returns string
 */
export const getCustomParams = (options) => {
  const { xes = null, subject = null, grade = null } = options
  if (isGaozhongBiz()) { // 高中
    return grade
  } else if (isWangxiaoBiz() || isSuZhiBiz()) { // 网校、素质
    return xes
  } else if (isXuekeBiz()) { // 新学科
    return subject
  } else {
    throw Error('请检查（biz|域名）是否正确！')
  }
}

/**
 * 返回url的query对象
 * @param {string} url
 * @returns object
 */
export const getURLQueries = url =>
  (url.match(/([^?=&]+)(=([^&]*))/g) || []).reduce(
    (a, v) => (
      // eslint-disable-next-line no-sequences
      (a[v.slice(0, v.indexOf('='))] = v.slice(v.indexOf('=') + 1)), a
    ),
    {}
  )

/**
 *  将 object 对象转化为 url 字符串
 * @param {object} options
 * @returns string
 */
export const getObjectToString = (options = {}) => {
  return Object.keys(options).reduce((pre, cur) => {
    pre.push(cur + '=' + options[cur])
    return pre
  }, []).join('&')
}

/**
 *  对 url 添加 query 参数，注：相同[key]会覆盖
 * @param {string} url
 * @param {object} options
 * @param {boolean} encode
 * @returns string
 */
export const setURLQueries = (url, options = {}, encode = false) => {
  if (!url) throw Error('url不能为空!')
  const query = getURLQueries(url)
  const host = url.split('?')[0]
  const queries = { ...query, ...options }
  let str = getObjectToString(queries)
  str = encode ? encodeURIComponent(str) : str
  return host + (str ? '?' + str : '')
}

/**
 * 对传入的url添加 biz 参数
 * @param {string} url
 * @param {boolean} flag - 是否需要添加biz参数
 * @returns url
 */
export const setQueryBizToUrl = (url, flag = true) => {
  const protocol = window.location.protocol
  const _url = /^http/.test(url) ? url : protocol + url
  const { origin, pathname, search, hash } = new URL(_url)
  const str = `${QUERY_BIZID_NAME}=${getCurrentBizId()}`
  const queries = getURLQueries(search)
  if (!queries[QUERY_BIZID_NAME] && flag) {
    const _search = search ? search + '&' + str : '?' + str
    return `${origin}${pathname}${_search}${hash}`
  } else {
    return url
  }
}

/**
 * 校验是否是合法的bizId
 * @param {number} bizId
 */
export const isValidBizId = biz => ([BIZ_GAOZHOGN, BIZ_SUZHI, BIZ_XINXUEKE, BIZ_WANGXIAO].indexOf(+biz) > -1)

// 全局添加biz query参数: 仅限于vue-router
export const addRouterBizQuery = (router) => {
  router.beforeEach((to, from, next) => {
    const bizId = to.query[QUERY_BIZID_NAME]
    const curId = getCurrentBizId()
    if (!bizId || !isValidBizId(bizId) || bizId !== curId) {
      const query = {
        ...to.query,
        [QUERY_BIZID_NAME]: curId
      }
      next &&
        next({
          ...to,
          query: query
        })
    } else {
      next && next()
    }
  })
}

// 设置页面的favicon
export const setSiteFavicon = () => {
  const link = getCustomParams({
    xes: 'https://static0.xesimg.com/xueersi-mall-common/images/image-logo-ico.ico',
    grade: 'https://static0.xesimg.com/service-project/logo/grade.ico',
    subject: 'https://static0.xesimg.com/service-project/logo/x9.ico'
  })
  setHeadFavicon(link)
}



// WEBPACK FOOTER //
// ./node_modules/@xes/trade-toolkit/src/business.js